/**
 * The sounds a session makes, and the reason you may not hear the first one.
 *
 * Gordon, after using the communicator: "whenever IT request the sharing or
 * there is a message - there should be a ping or a sound/call to answer." He
 * is right, and the reason he is right is that a prompt nobody hears is a
 * prompt nobody answers: the user is looking at the broken machine, not at
 * Jarvis.
 *
 * NO AUDIO FILES. Every tone here is built from oscillators, so there is
 * nothing to fetch, nothing to cache, nothing to go missing behind a proxy and
 * no second place for a 404 to hide. Two short sine tones are also a better
 * notification than most recorded ones: short, quiet, and nothing like a
 * ringtone somebody will be embarrassed by in an open-plan office.
 *
 * THE PART THAT WILL CATCH SOMEBODY OUT. Browsers refuse to play audio until
 * the page has had a real user gesture - a click, a key, a tap. Arriving on
 * the session page by following a link does NOT count: the gesture happened on
 * the page before. So the first ring can be silently refused, and a silently
 * refused ring is precisely the failure this whole change exists to prevent.
 *
 * Two things follow, and both are deliberate:
 *
 *   `unlock()` is called on the first interaction of any kind, so in practice
 *   a user who has clicked anything at all is already unlocked.
 *
 *   `blocked()` reports honestly when the browser is still refusing, so the
 *   page can SHOW something instead of trusting a sound nobody heard. The
 *   visible prompt and the title flash never depend on audio working.
 *
 * Nothing in here throws. A browser with no Web Audio at all - or one that has
 * decided today is the day to refuse - degrades to a page that still rings
 * visually, which is the behaviour of a page that cannot make a noise rather
 * than a broken one.
 */

import { soundFor, setMasterSound } from './notifyPrefs';

let ctx = null;
let blockedUntilGesture = false;
let ringTimer = null;
let ringsLeft = 0;

/** A bare, cheap context. Created lazily: constructing one eagerly on every
 *  page load is how a browser decides an application is noisy. */
function context() {
  if (ctx) return ctx;
  const Ctor = typeof window !== 'undefined'
    && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

/**
 * Whether sound is wanted - for one kind of notice, or at all.
 *
 * SERVER-SIDE SINCE 117: the master switch and each kind's "sound & pop-up"
 * are the person's notification preferences (lib/notifyPrefs, the
 * Notifications page), so they follow them from browser to browser. This
 * browser's own copy (localStorage 'jarvis_session_sound', which is all
 * there was before) is the fallback while the server has not answered, when
 * it cannot be asked, and before the migration - so a page that cannot read
 * a preference is a page with the remembered one, not a broken page.
 *
 *   'chat'            a message (ping)
 *   'todo_reminders'  a to-do reminder going off (nudge)
 *   'calls'           a call or a request to share the screen (the ring)
 */
export function soundWanted(kind = null) {
  return soundFor(kind);
}

/** The master switch, from a screen: this browser at once, the server behind it. */
export function setSoundWanted(on) {
  setMasterSound(on);
  if (!on) stopRinging();
  return on;
}

/** True when the browser is refusing to play until somebody interacts. */
export const blocked = () => blockedUntilGesture
  || (ctx ? ctx.state === 'suspended' : false);

/**
 * Called on the first user gesture. Safe to call as often as you like.
 *
 * Resuming a suspended context is the whole job; the silent one-sample tone
 * afterwards is what actually satisfies Safari, which resumes the context
 * happily and then still refuses the first real sound.
 */
export function unlock() {
  const c = context();
  if (!c) return;
  const done = () => {
    blockedUntilGesture = false;
    try {
      const g = c.createGain();
      g.gain.value = 0;
      g.connect(c.destination);
      const o = c.createOscillator();
      o.connect(g);
      o.start();
      o.stop(c.currentTime + 0.01);
    } catch { /* nothing to be done, and nothing worth saying */ }
  };
  if (c.state === 'suspended') {
    const resumed = c.resume();
    if (resumed && typeof resumed.then === 'function') {
      resumed.then(done).catch(() => { blockedUntilGesture = true; });
    } else {
      done();
    }
  } else {
    done();
  }
}

/**
 * One tone.
 *
 * Attack and release are shaped rather than switched, because a gain that
 * jumps from 0 to full produces a click at both ends - which sounds like a
 * fault rather than a notification, and is the single thing that makes a
 * generated tone sound cheap.
 */
function tone(c, { at = 0, freq = 880, ms = 140, gain = 0.07 } = {}) {
  try {
    const t0 = c.currentTime + at;
    const osc = c.createOscillator();
    const amp = c.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    amp.gain.setValueAtTime(0.0001, t0);
    amp.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
    osc.connect(amp);
    amp.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + ms / 1000 + 0.02);
  } catch {
    /* A context that has been closed under us. Not worth a word. */
  }
}

/**
 * A chat message arrived.
 *
 * Deliberately quiet and deliberately short: this fires on every message, and
 * a notification that is a fraction too loud the first time is a notification
 * somebody turns off before the second.
 */
export function ping(kind = 'chat') {
  if (!soundWanted(kind)) return false;
  const c = context();
  if (!c || c.state === 'suspended') { blockedUntilGesture = true; return false; }
  tone(c, { at: 0, freq: 880, ms: 90, gain: 0.05 });
  tone(c, { at: 0.085, freq: 1175, ms: 110, gain: 0.05 });
  return true;
}

/**
 * A to-do is due (batch 3's reminders).
 *
 * Three soft notes going up and settling: friendlier than the call's ring,
 * because nobody is waiting on the line, and unlike the chat ping, because
 * "something is due" should not be mistaken for "somebody wrote". Once, not
 * repeated - the card stays on screen, and that is what keeps asking.
 * Respects the same "sound off" as everything else here.
 */
export function nudge() {
  if (!soundWanted('todo_reminders')) return false;
  const c = context();
  if (!c || c.state === 'suspended') { blockedUntilGesture = true; return false; }
  tone(c, { at: 0, freq: 784, ms: 120, gain: 0.06 });
  tone(c, { at: 0.13, freq: 988, ms: 120, gain: 0.06 });
  tone(c, { at: 0.26, freq: 1175, ms: 220, gain: 0.06 });
  return true;
}

/**
 * Somebody is asking for an answer.
 *
 * A rising pair rather than a falling one, because a falling interval reads as
 * "finished" and this one means "you are needed".
 */
function ringOnce() {
  const c = context();
  if (!c || c.state === 'suspended') { blockedUntilGesture = true; return false; }
  tone(c, { at: 0, freq: 660, ms: 180, gain: 0.08 });
  tone(c, { at: 0.2, freq: 990, ms: 260, gain: 0.08 });
  return true;
}

/**
 * Ring until answered, or until it has rung enough.
 *
 * IT STOPS ON ITS OWN, after eight rings - about twenty seconds. A ring that
 * continues until somebody attends to it is a ring somebody mutes permanently,
 * and then the next one does not sound either. The visible prompt stays up
 * after the sound has given up, which is the right way round: the question is
 * still open, it has simply stopped shouting.
 *
 * Returns whether the sound actually started, so the caller can show the
 * prompt more insistently when it did not.
 */
export function startRinging({ times = 8, everyMs = 2500, kind = 'calls' } = {}) {
  stopRinging();
  if (!soundWanted(kind)) return false;
  ringsLeft = times;
  const first = ringOnce();
  ringsLeft -= 1;
  ringTimer = setInterval(() => {
    if (ringsLeft <= 0) { stopRinging(); return; }
    ringsLeft -= 1;
    ringOnce();
  }, everyMs);
  return first;
}

export function stopRinging() {
  if (ringTimer) clearInterval(ringTimer);
  ringTimer = null;
  ringsLeft = 0;
}

export const ringing = () => ringTimer !== null;

/**
 * A short buzz on a phone, where there is one.
 *
 * Separate from the sound because it is the half that works with the handset
 * face down and on silent, which is where a phone usually is. Ignored
 * everywhere else, including desktop Safari, which declares the function and
 * does nothing.
 */
export function buzz(pattern = [120, 90, 120]) {
  try {
    if (navigator?.vibrate) navigator.vibrate(pattern);
  } catch { /* declared and refused, which is fine */ }
}

/**
 * Flash the tab title until the page is looked at.
 *
 * The notification that works when the sound is muted, the tab is in the
 * background and the phone is in a pocket - which is most of the time. The
 * original title is captured once and always restored, because a page that
 * leaves "(1) IT needs you" in the tab after the moment has passed is worse
 * than one that never flashed at all.
 */
let flashTimer = null;
let originalTitle = null;

export function flashTitle(message) {
  if (typeof document === 'undefined') return;
  stopFlashing();
  originalTitle = document.title;
  let on = false;
  flashTimer = setInterval(() => {
    on = !on;
    document.title = on ? message : originalTitle;
  }, 1200);
  document.title = message;
}

export function stopFlashing() {
  if (flashTimer) clearInterval(flashTimer);
  flashTimer = null;
  if (originalTitle !== null) {
    document.title = originalTitle;
    originalTitle = null;
  }
}

/** Everything off, for an unmount. Leaving a timer behind rings an empty room. */
export function silence() {
  stopRinging();
  stopFlashing();
}
